/**
 * Stack persistence - the navigation stack survives a page reload.
 *
 * The WebView's content process can be killed for memory (iOS Jetsam) and
 * reloaded by Capacitor; a deploy can reload for fresh chunks. Either way the
 * URL survives but the stack beneath it used to vanish, dead-ending the user.
 * The entries are mirrored to sessionStorage and rebuilt on boot when they
 * still describe where the page is.
 *
 * NORTHSTARS:
 * - Never dead-ending is a promise about state, not about DOM
 * - Trust the platform - sessionStorage is per tab and per session
 * - Pure - the DOM-free part lives here and is tested
 */

import type { StackEntry } from "@/contexts/NavigationStackContext";

export const STACK_STORAGE_KEY = "stasht_stack_v1";

const STORAGE_VERSION = 1;

/** Location state can carry whole posts; the cache has those. Entries persist without it. */
export const serializeStack = (stack: readonly StackEntry[]): string =>
  JSON.stringify({
    v: STORAGE_VERSION,
    stack: stack.map(entry => ({
      id: entry.id,
      pathname: entry.pathname,
      search: entry.search,
      hash: entry.hash,
      underIdx: entry.underIdx,
    })),
  });

const isEntry = (value: unknown): value is Omit<StackEntry, "state"> => {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === "string" &&
    typeof entry.pathname === "string" &&
    typeof entry.search === "string" &&
    typeof entry.hash === "string" &&
    (entry.underIdx === null || typeof entry.underIdx === "number")
  );
};

/**
 * The stored stack, whenever it is well-formed. It need not end at the
 * current URL: a kill can land between a navigation and its persist, and
 * StackedRouter already reconciles a stack against the URL (pushes the new
 * top, pops back to an inner entry, clears on a tab). The session is the
 * user's own history, so nothing here is stale in a way that matters.
 */
export const restoreStack = (raw: string | null): StackEntry[] | null => {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const { v, stack } = parsed as { v?: unknown; stack?: unknown };
  if (v !== STORAGE_VERSION || !Array.isArray(stack) || stack.length === 0) return null;
  if (!stack.every(isEntry)) return null;
  return stack.map(entry => ({ ...entry, state: null, restored: true }));
};
